export const reportDesignerArticles = [
    {
        id: 'report-designer.overview',
        title: 'Report Designer Overview',
        category: 'Report Designer',
        order: 1,
        summary: 'Design and print Bills of Quantities and branded Purchase Orders',
        content: `
The Report Designer is a drag-and-drop, banded report builder embedded in Costed. Use it to lay out, preview, and print a job's **Bill of Quantities** or a branded **Purchase Order** — and to save your own layout as a reusable template.

**Two report types** (top toolbar):

- **Bill of Quantities** — a job-level report grouped by cost centre.
- **Purchase Order** — a single supplier's order, ready to preview, download as PDF, or email.

**Two data modes:**

- **Live job** (the default) — pulls a real job's BoQ / orders, with your saved company logo on the PO.
- **Sample** — bundled demo data that works offline, handy for trying out layout changes.

Your choices — report type, **Design / Preview**, and the selected job — are remembered between visits.

**Who can do what:** a **viewer** opens and prints every report but cannot save a layout. Any other role saves layouts as **drafts** (up to 512 KB each, 200 per workspace). An **owner or admin** publishes a layout, sets the workspace default for a report type, and changes a published layout in place — anyone else who edits a published layout saves their version as a new draft. Layouts made by the assistant are always drafts. A rendered PDF never runs scripts.
    `,
        expandableSections: [
            {
                title: 'Selecting a job',
                content: `
In **Live job** mode, use the **Job** search box at the top. It matches on **job number, name, or site address** — type any words in any order. The job's **site address** is shown next to the box once selected.

- Press **Enter** to select the top (or exact-number) match.
- The report loads automatically as soon as you pick a job — there is no separate "Load" button.
        `,
            },
            {
                title: 'Design vs Preview',
                content: `
- **Design** — the editing canvas: bands, the element toolbox, rulers, and the Properties / Data panels.
- **Preview** — renders the report with the current data exactly as it will print.

Use **Download PDF** at any time to export what you see.
        `,
            },
        ],
        keywords: ['report designer', 'report writer', 'bill of quantities', 'purchase order', 'PDF', 'print', 'template'],
        route: '/report-designer',
        relatedArticles: ['report-designer.purchase-order', 'report-designer.design', 'orders.overview', 'boq.overview'],
        screenshots: [
            {
                src: 'report-designer/report-designer-overview.png',
                srcAnnotated: 'report-designer/report-designer-overview-annotated.png',
                alt: 'The Report Designer editing a Purchase Order',
                caption: 'The Report Designer: top toolbar, element Toolbox, the banded canvas, and the Properties / Data panels.',
                callouts: [
                    { number: 1, label: 'Report type & data mode', description: 'Switch between Bill of Quantities and Purchase Order, and between Sample and Live job data.' },
                    { number: 2, label: 'Job & PO selectors', description: 'Search a job by number, name or address; for a Purchase Order pick a Cost Centre and Load.' },
                    { number: 3, label: 'Design / Preview / PDF', description: 'Edit on the canvas, preview the rendered report, or download it as a PDF.' },
                    { number: 4, label: 'Toolbox', description: 'Drag elements (Label, Text Box, Line, Box, Picture, Chart, Bar Code, Sub-report) onto a band.' },
                    { number: 5, label: 'Properties / Data', description: 'Position, Appearance and field bindings for the selected element.' },
                ],
            },
        ],
    },
    {
        id: 'report-designer.purchase-order',
        title: 'Printing & Emailing a Purchase Order',
        category: 'Report Designer',
        order: 2,
        summary: 'Pick one supplier order by Cost Centre + Load, then preview, PDF or email it',
        content: `
Choose **Purchase Order** in the toolbar. A Purchase Order is a single supplier's document, so you select exactly one:

1. **Cost Centre** — searchable (type any words in any order).
2. **Load** — defaults to **1**; pick another load if the cost centre has more than one.

The selected order (number and supplier) is shown beside the pickers. The layout matches Costed's branded PO — blue masthead, your company logo, supplier / deliver-to cards, line-item table, and a GST-inclusive total.
    `,
        expandableSections: [
            {
                title: 'Headings & sub-headings',
                content: `
Lines whose **unit** marks them as a heading or sub-heading (e.g. "Heading" / "HD", "Sub Heading" / "SH") print as **full-width banded rows** — bold, highlighted, with no Code / Qty / Unit / Price columns — to break the order into sections. Regular item lines show all columns, and long descriptions wrap and grow their row so nothing overlaps.
        `,
            },
            {
                title: 'Download PDF & Email to supplier',
                content: `
- **Download PDF** exports the current PO (named \`PO-<order number>\`).
- **Email to supplier** (Live mode only) sends the order through Costed's existing send-order flow, using the supplier's email on the order. You're asked to confirm before it sends.
        `,
            },
        ],
        keywords: ['purchase order', 'PO', 'supplier', 'email order', 'load', 'cost centre', 'heading', 'sub-heading', 'PDF'],
        route: '/report-designer',
        relatedArticles: ['report-designer.overview', 'orders.send', 'orders.overview'],
        screenshots: [
            {
                src: 'report-designer/report-designer-purchase-order.png',
                srcAnnotated: 'report-designer/report-designer-purchase-order-annotated.png',
                alt: 'A rendered Purchase Order with heading and sub-heading bands',
                caption: 'A previewed Purchase Order: branded masthead and logo, Supplier / Deliver-To cards, banded heading & sub-heading rows, and a GST-inclusive total.',
                callouts: [
                    { number: 1, label: 'Masthead & logo', description: 'Blue masthead with the order number and your saved company logo.' },
                    { number: 2, label: 'Supplier / Deliver To', description: 'Cards showing the supplier and the delivery (job) details.' },
                    { number: 3, label: 'Heading & sub-heading bands', description: 'Lines whose unit marks them as a heading/sub-heading (e.g. "SITE FACILITIES", "Toilet hire & servicing") print full-width and banded, with no Code/Qty/Unit/Price columns.' },
                    { number: 4, label: 'GST-inclusive total', description: 'Subtotal (item count), GST and the blue Inc-GST grand total.' },
                ],
            },
        ],
    },
    {
        id: 'report-designer.design',
        title: 'Designing & Styling a Report',
        category: 'Report Designer',
        order: 3,
        summary: 'Bands, elements, the Properties panel, borders & rounded corners, and saving templates',
        content: `
The Design canvas is organised into **bands** (Group Header, Detail, Group Footer, Page Footer, …). Drag elements from the **Toolbox** (Label, Text Box, Line, Box, Picture, Chart, Bar Code, Sub-report) onto a band, then position and style them in the **Properties** panel. The **Data** tab lists the fields you can drop into expressions.
    `,
        expandableSections: [
            {
                title: 'Appearance: borders & rounded corners',
                content: `
Select a **Box** (or text) element and open the **Appearance** section of the Properties panel:

- **Corner radius (px)** — rounds the element's corners (e.g. \`6\`). Set to \`0\` for square corners.
- **Border left / top / right / bottom** — each takes CSS shorthand: **width style colour**, e.g. \`4px solid #0056b3\`. Leave a side blank for no border.

This is how the PO's supplier card gets its rounded corners and blue left band (Corner radius \`6\`, Border left \`4px solid #0056b3\`). **Background colour** has its own toggle + colour picker above.
        `,
            },
            {
                title: 'Editing a sub-report (drill-down) — double-click to edit its columns',
                content: `
Some reports embed a **sub-report** — a master–detail drill-down. In **Aged Debtors**, for example, each debtor row expands into the individual outstanding **claims** that make up its balance.

On the Design canvas a sub-report shows as a **dashed placeholder** (e.g. \`⊞ Sub-report → claims · double-click to edit its columns\`). It only fills with its child rows in **Preview** (and PDF) — on paper every master row prints with its detail rows underneath, like a statement that lists every line. It is *always-expanded*, not a click-to-collapse grid.

To change the child columns, **double-click the placeholder** to drill in:

- A breadcrumb appears under the ribbon (e.g. **← Aged Debtors › claims**) and the whole workspace — Toolbox, canvas, Properties and Data — now acts on the *child* report.
- Add, move and restyle the child's columns exactly as you would the master.
- Click the breadcrumb (or press **Esc**) to return. Your child edits are written back into the parent and saved with the template.
        `,
            },
            {
                title: 'Saving & reusing a template',
                content: `
- **Save** / **Save As** store your layout. Next time, the **published** layout for the report type is applied (or, if none is published, the most recent draft), while the live data (job, supplier, totals) refreshes.
- **New** starts a fresh layout; **Open** loads a saved one.
- **AI context** generates a starting layout from a prompt.
- **↺ Reset layout** removes the saved templates for the current report type **for the whole workspace** (owner or admin) and restores the built-in design — handy if an old saved layout is hiding a newer feature (such as the claims drill-down).
        `,
            },
        ],
        keywords: ['design', 'bands', 'properties', 'border', 'rounded corners', 'corner radius', 'box', 'template', 'save', 'styling', 'sub-report', 'subreport', 'drill-down', 'aged debtors', 'claims', 'master-detail'],
        route: '/report-designer',
        relatedArticles: ['report-designer.overview', 'report-designer.purchase-order'],
        screenshots: [
            {
                src: 'report-designer/report-designer-overview.png',
                srcAnnotated: 'report-designer/report-designer-design-annotated.png',
                alt: 'The Report Designer canvas, Toolbox and Properties panel',
                caption: 'Drag elements from the Toolbox onto a band, then style them in Properties → Appearance (background, corner radius, per-side borders).',
                callouts: [
                    { number: 1, label: 'Bands', description: 'Group Header, Detail, Group Footer and Page Footer sections that the engine repeats and positions.' },
                    { number: 2, label: 'Toolbox', description: 'The elements you can add: Label, Text Box, Line, Box, Picture, Chart, Bar Code, Sub-report.' },
                    { number: 3, label: 'Appearance', description: 'For the selected box/text element: Background colour, Corner radius and per-side Border controls.' },
                ],
            },
            {
                src: 'report-designer/report-designer-subreport-drill.png',
                alt: 'Drilled into the Aged Debtors claims sub-report, editing its columns',
                caption: 'Double-clicking the sub-report placeholder drills into the child report. The breadcrumb (← Aged Debtors › claims) returns to the master; the canvas, Toolbox and Properties now edit the claim columns.',
            },
        ],
    },
    {
        id: 'report-designer.expressions',
        title: 'Expressions & operators',
        category: 'Report Designer',
        order: 4,
        summary: 'The formula language: operators, functions and system values used in fields, Visible-when and Conditional formatting',
        content: `
Anywhere you type a **formula** — a Text element's **Expression**, a band's **Visible when**, a group/sort key, a calculated field, or a **Conditional formatting** rule's **When** — you use the same small expression language.

**Field references** use square brackets so names can contain spaces: \`[Unit Price]\`, \`[Type]\`. Text is quoted with **single or double quotes**: \`"REC"\` or \`'REC'\`.

> **There is no \`Like\` operator** (that's SQL). To match text use \`=\` for an exact match, or the \`Contains\` / \`StartsWith\` / \`EndsWith\` functions for a partial match.
> Exact: \`[Type] = "REC"\` · Partial: \`Contains([Description], "Credit")\`
>
> **Names aren't case-sensitive** — \`[TranDate]\` and \`[trandate]\` both resolve, and functions too (\`Iif\` = \`IIF\`). The chips under **Insert field** paste the exact name for you.

**Operators**

| | |
|---|---|
| Equals / not equals | \`=\`  \`==\`  ·  \`<>\`  \`!=\` |
| Compare | \`<\`  \`<=\`  \`>\`  \`>=\` |
| And / Or / Not | \`&&\`  \`||\`  \`!\`  (also \`and\` / \`or\` / \`not\`) |
| Maths | \`+\`  \`-\`  \`*\`  \`/\`  \`%\`  (join text with \`+\`) |

**Examples**

\`\`\`
[Units] * [Price]
[Type] = "REC"
Iif([Balance] > 0, "Outstanding", "Settled")
Sum([Debit]) - Sum([Credit])
RunningSum([Debit] - [Credit])
"Page " + [PageNumber] + " of " + [PageCount]
DateDiff([TranDate], Today())
\`\`\`
    `,
        expandableSections: [
            {
                title: 'Functions',
                content: `
**Logic / text-or-number:** \`Iif(cond, a, b)\`, \`Coalesce(a, b, …)\` (first non-blank), \`IsNull(value, fallback)\`, \`IsBlank(v)\`, \`Format(value, "code")\`.

**Text** (positions are 1-based): \`Concat\`, \`Upper\`, \`Lower\`, \`Trim\`, \`Len\`, \`Left(s, n)\`, \`Right(s, n)\`, \`Mid(s, start[, len])\`, \`Replace(s, find, repl)\`, \`Contains(s, sub)\`, \`StartsWith(s, sub)\`, \`EndsWith(s, sub)\`, \`IndexOf(s, sub)\`, \`PadLeft/PadRight(s, len[, ch])\`.

**Dates** (month is 1-12; an invalid date is blank): \`Today()\`, \`DateValue(v)\`, \`Year(v)\`, \`Month(v)\`, \`Day(v)\`, \`AddDays(v, n)\`, \`AddMonths(v, n)\`, \`DateDiff(a, b)\` (whole days from a to b — e.g. days overdue = \`DateDiff([Due], Today())\`).

**Maths:** \`Round(n[, dp])\`, \`Abs\`, \`Floor\`, \`Ceil\`, \`Sqrt\`, \`Pow(x, y)\`, \`Mod(a, b)\`, \`Sign\`, \`Trunc\`.

**Totals** (over the rows in the band's scope — a group's rows in a group footer, all rows in the report footer): \`Sum(expr)\`, \`Avg(expr)\`, \`Count()\`, \`Min(expr)\`, \`Max(expr)\`.

**Running totals** (only the rows up to the current detail row, reset per group): \`RunningSum(expr)\`, \`RunningCount()\`, \`RunningAvg(expr)\`.
        `,
            },
            {
                title: 'System values',
                content: `
Available in any formula:

- \`PageNumber\`, \`PageCount\` — the current page and total pages.
- \`RowNumber\` — a continuous line number across the whole report.
- \`GroupRowNumber\` — a line number that restarts at 1 inside each group.
- \`GroupKey\` — the current group's key value (in group headers/footers).
- \`Now\` — the date/time the report was run; \`ReportName\` — the report's name.

Tip: stripe rows with a Conditional-formatting rule \`Mod([RowNumber], 2) = 0\`.
        `,
            },
            {
                title: 'Format codes',
                content: `
Set a Text element's **Format** (or use \`Format(value, "code")\`). \`N\` is the number of decimals:

- \`cN\` — currency with separators, e.g. \`c2\` → \`$1,234.50\`
- \`nN\` — number with separators, e.g. \`n0\` → \`1,234\`
- \`fN\` — fixed decimals, no separators
- \`pN\` — percent, e.g. \`p1\` → \`12.5%\`
- \`d\` / \`D\` — short / long localised date

**Build your own date format** (case-insensitive tokens): \`d\` \`dd\` \`ddd\` \`dddd\` (day), \`m\` \`mm\` \`mmm\` \`mmmm\` (month), \`yy\` \`yyyy\` (year). For example:

- \`dd/MM/yyyy\` → \`07/03/2026\`
- \`d MMM yyyy\` → \`7 Mar 2026\`
- \`MMMM yyyy\` → \`March 2026\`
- \`yyyy-MM-dd\` → \`2026-03-07\`
        `,
            },
        ],
        keywords: ['expression', 'operator', 'formula', 'function', 'Like', 'Contains', 'conditional formatting', 'RunningSum', 'RowNumber', 'DateDiff', 'Iif', 'Sum'],
        route: '/report-designer',
        relatedArticles: ['report-designer.design', 'report-designer.overview'],
    },
];
